import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  ArrowRight,
  CheckCircle2,
  Circle,
  Search,
  Settings2,
  Sparkles,
} from "lucide-react";
import {
  SETTINGS_CATEGORIES,
  flattenSettingsItems,
  filterSettingsHubItems,
} from "../../config/settingsNavigation";
import { useSetupProgress } from "../../Moduels/Context/SetupProgressContext";
import { useAttendanceShiftNav } from "../../hooks/useAttendanceShiftNav";
import "../../styles/settingsHub.css";

function canSeeItem(item, role) {
  if (!item.roles?.length) return true;
  return item.roles.includes(role);
}

function getItemStatus(item, progress) {
  if (!item.statusKey || !progress) return null;
  if (progress.statusMap && item.statusKey in progress.statusMap) {
    return progress.statusMap[item.statusKey] ? "done" : "pending";
  }
  const found = progress.items?.find((i) => i.id === item.statusKey);
  if (!found) return null;
  return found.complete ? "done" : "pending";
}

function SettingCard({ item, category, role, progress, onInlineSelect }) {
  const status = getItemStatus(item, progress);
  const Icon = item.icon;
  const isInline = Boolean(item.inlineTab);

  const inner = (
    <>
      <div className="settings-card-top">
        <div className={`settings-card-icon ${category.accent}`}>
          <Icon className="h-5 w-5" strokeWidth={1.75} />
        </div>
        <div className="min-w-0 flex-1">
          <h3 className="settings-card-title">{item.label}</h3>
          {item.optional ? (
            <span className="settings-badge optional mt-1">Optional</span>
          ) : null}
        </div>
      </div>
      <p className="settings-card-desc">{item.description}</p>
      {item.badges?.length ? (
        <div className="settings-method-tags">
          {item.badges.map((b) => {
            const BIcon = b.icon;
            return (
              <span key={b.label} className="settings-badge feature">
                {BIcon ? <BIcon className="h-3 w-3" /> : null}
                {b.label}
              </span>
            );
          })}
        </div>
      ) : null}
      <div className="settings-card-footer">
        {status === "done" ? (
          <span className="settings-badge done">
            <CheckCircle2 className="h-3 w-3" />
            Configured
          </span>
        ) : null}
        {status === "pending" ? (
          <span className="settings-badge pending">
            <Circle className="h-3 w-3" />
            Setup needed
          </span>
        ) : null}
        {item.highlight && !status ? (
          <span className="settings-badge feature">
            <Sparkles className="h-3 w-3" />
            Recommended
          </span>
        ) : null}
        <ArrowRight className="settings-card-arrow h-4 w-4" />
      </div>
    </>
  );

  const className = ["settings-card", item.highlight ? "highlight" : ""].filter(Boolean).join(" ");

  if (isInline && onInlineSelect) {
    return (
      <button type="button" className={className} onClick={() => onInlineSelect(item.inlineTab)}>
        {inner}
      </button>
    );
  }

  return (
    <Link to={item.path} className={className}>
      {inner}
    </Link>
  );
}

export default function SettingsConfigurationHub({ role = "employee", onInlineSelect, activeCategory, onCategoryChange }) {
  const { progress } = useSetupProgress();
  const shiftNavEnabled = useAttendanceShiftNav();
  const [search, setSearch] = useState("");
  const [localCategory, setLocalCategory] = useState(activeCategory || "all");

  const selectedCategory = activeCategory ?? localCategory;
  const setCategory = onCategoryChange ?? setLocalCategory;

  const visibleCategories = useMemo(
    () =>
      SETTINGS_CATEGORIES.map((cat) => ({
        ...cat,
        items: filterSettingsHubItems(cat.items).filter(
          (item) =>
            canSeeItem(item, role) && (!item.requiresShiftAttendance || shiftNavEnabled)
        ),
      })).filter((cat) => cat.items.length > 0),
    [role, shiftNavEnabled]
  );

  const flatItems = useMemo(() => flattenSettingsItems(visibleCategories), [visibleCategories]);

  const filteredCategories = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) {
      if (selectedCategory === "all") return visibleCategories;
      return visibleCategories.filter((c) => c.id === selectedCategory);
    }
    const matched = flatItems.filter(
      (item) =>
        item.label.toLowerCase().includes(q) ||
        item.description.toLowerCase().includes(q) ||
        item.categoryLabel.toLowerCase().includes(q)
    );
    const byCat = {};
    matched.forEach((item) => {
      if (!byCat[item.categoryId]) {
        byCat[item.categoryId] = visibleCategories.find((c) => c.id === item.categoryId);
      }
    });
    return Object.values(byCat).filter(Boolean);
  }, [search, selectedCategory, visibleCategories, flatItems]);

  const configuredCount = progress?.items?.filter((i) => i.complete).length ?? 0;
  const totalTracked = progress?.items?.length ?? 0;
  const pct = progress?.percent ?? 0;

  const topAlerts = (progress?.alerts || []).slice(0, 3);

  return (
    <div className="settings-platform">
      <header className="settings-hero">
        <div className="settings-hero-inner">
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div>
              <div className="settings-hero-eyebrow">
                <Settings2 className="h-3.5 w-3.5" />
                Configuration Centre
              </div>
              <h2 className="settings-hero-title">All settings in one place</h2>
              <p className="settings-hero-sub">
                Organisation profile, payroll, attendance, statutory compliance, and access control —
                manage everything from a single, unified platform.
              </p>
            </div>
            {!progress?.loading && totalTracked > 0 ? (
              <div
                className="settings-progress-ring"
                style={{ "--pct": pct }}
                title={`${pct}% setup complete`}
              >
                <span>{pct}%</span>
              </div>
            ) : null}
          </div>

          <div className="settings-hero-stats">
            <div className="settings-hero-stat">
              <div>
                <div className="settings-hero-stat-value">{visibleCategories.length}</div>
                <div className="settings-hero-stat-label">Categories</div>
              </div>
            </div>
            <div className="settings-hero-stat">
              <div>
                <div className="settings-hero-stat-value">{flatItems.length}</div>
                <div className="settings-hero-stat-label">Settings</div>
              </div>
            </div>
            {!progress?.loading ? (
              <div className="settings-hero-stat">
                <div>
                  <div className="settings-hero-stat-value">
                    {configuredCount}/{totalTracked}
                  </div>
                  <div className="settings-hero-stat-label">Configured</div>
                </div>
              </div>
            ) : null}
            {progress?.payrollReady ? (
              <div className="settings-hero-stat">
                <CheckCircle2 className="h-5 w-5 text-emerald-300" />
                <div>
                  <div className="settings-hero-stat-value text-emerald-200">Ready</div>
                  <div className="settings-hero-stat-label">Payroll</div>
                </div>
              </div>
            ) : null}
          </div>

          <div className="settings-search-wrap">
            <Search className="settings-search-icon h-4 w-4" />
            <input
              type="search"
              className="settings-search-input"
              placeholder="Search settings…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              aria-label="Search settings"
            />
          </div>
        </div>
      </header>

      {topAlerts.length > 0 ? (
        <div className="settings-alerts">
          {topAlerts.map((alert) => (
            <div key={alert.id} className={`settings-alert ${alert.level === "block" ? "warn" : "warn"}`}>
              {alert.message}
              <Link to={alert.path}>Configure →</Link>
            </div>
          ))}
        </div>
      ) : null}

      <div className="settings-layout">
        <aside className="settings-cat-nav">
          <div className="settings-cat-nav-head">Categories</div>
          <button
            type="button"
            className={`settings-cat-btn ${selectedCategory === "all" ? "active" : ""}`}
            onClick={() => setCategory("all")}
          >
            All Settings
            <span className="settings-cat-btn-count">{flatItems.length}</span>
          </button>
          {visibleCategories.map((cat) => (
            <button
              key={cat.id}
              type="button"
              className={`settings-cat-btn ${selectedCategory === cat.id ? "active" : ""}`}
              onClick={() => setCategory(cat.id)}
            >
              {cat.label}
              <span className="settings-cat-btn-count">{cat.items.length}</span>
            </button>
          ))}
        </aside>

        <div className="settings-main">
          <AnimatePresence mode="wait">
            {filteredCategories.length === 0 ? (
              <motion.div
                key="empty"
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0 }}
                className="settings-empty"
              >
                <Search className="mx-auto h-10 w-10 text-slate-300" />
                <p className="settings-empty-title">No settings found</p>
                <p className="text-sm">Try a different search term or browse all categories.</p>
              </motion.div>
            ) : (
              <motion.div
                key={search + selectedCategory}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.2 }}
              >
                {filteredCategories.map((cat) => (
                  <section key={cat.id} id={`settings-${cat.id}`} className="settings-section">
                    <div className="settings-section-head">
                      <div>
                        <div className={`settings-section-accent ${cat.accent}`} />
                        <h3 className="settings-section-title">{cat.label}</h3>
                        <p className="settings-section-desc">{cat.description}</p>
                      </div>
                    </div>
                    <div className="settings-cards-grid">
                      {cat.items.map((item, idx) => (
                        <motion.div
                          key={item.id}
                          initial={{ opacity: 0, y: 12 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ delay: idx * 0.04, duration: 0.25 }}
                        >
                          <SettingCard
                            item={item}
                            category={cat}
                            role={role}
                            progress={progress}
                            onInlineSelect={onInlineSelect}
                          />
                        </motion.div>
                      ))}
                    </div>
                  </section>
                ))}
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>
    </div>
  );
}
